// l18n
import { TFunction } from 'i18next';

// mui
import { Grid, Typography } from '@mui/material';

// table
import { TableHeader } from '../../../ui/Table/Table';

// types
import {
	Account,
	ExpenseType,
	Transaction,
	TransactionStatus,
	TransactionType,
} from '../../../../@types';

// ui
import { Amount } from '../../../ui/Amount/Amount';
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { ChipTraceability } from '../../../ui/ChipTraceability/ChipTraceability';
import { IconCircleTransaction } from '../../../ui/IconCircleTransaction/IconCircleTransaction';
import { Time } from '../../../ui/Time/Time';
import { TypographyCaption } from '../../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../../ui/UserAvatarModified/UserAvatarModified';

// links
import { LinkAccount } from '../../../links/LinkAccount/LinkAccount';

// utils
import { translateTransactionType } from '../../../../utils/translations';
import { getTransactionDisplayValue } from '../../../../utils/transactions';

// props
export interface GetTransactionsHeadersParams {
	currency?: string;
	hasConversions?: boolean;
	headersIncluded?: string[];
	loan?: number;
	t: TFunction<'translation', undefined>;
}

export const getTransactionsHeaders = ({
	currency,
	hasConversions,
	headersIncluded = [],
	loan,
	t,
}: GetTransactionsHeadersParams): TableHeader[] => {
	const columns: TableHeader[] = [];

	if (headersIncluded.includes('type')) {
		columns.push({
			label: t('type'),
			value: 'type',
			render: (type: TransactionType, header: TableHeader, transaction: Transaction) => {
				return (
					<Grid alignItems="center" container wrap="nowrap" spacing={1}>
						<Grid item>
							<IconCircleTransaction size="sm" transaction={transaction} />
						</Grid>
						<Grid flex={1} item>
							<TypographyCaption id={transaction.id} />
							<Typography fontWeight={600}>{translateTransactionType(type, t)}</Typography>
						</Grid>
					</Grid>
				);
			},
		});
	}

	if (headersIncluded.includes('date')) {
		columns.push({
			label: t('date'),
			value: 'date',
			render: (date: Date, header: TableHeader, transaction: Transaction) => {
				return <Time date={new Date(date)} />;
			},
		});
	}

	if (headersIncluded.includes('recorded')) {
		columns.push({
			label: t('recorded'),
			value: 'created',
			render: (created: Date, header: TableHeader, transaction: Transaction) => {
				return <Time date={new Date(created)} />;
			},
		});
	}

	if (headersIncluded.includes('expenseType')) {
		columns.push({
			label: t('expenseType'),
			value: 'expenseType',
			render: (expenseType: ExpenseType) => {
				let name = t('na');
				if (expenseType && typeof expenseType === 'object' && expenseType.name) {
					name = expenseType.name;
				}
				return <Typography>{name}</Typography>;
			},
		});
	}

	if (headersIncluded.includes('evidenced')) {
		columns.push({
			label: t('evidenced'),
			value: 'traceability',
			render: (traceability: Transaction['traceability']) => {
				return <ChipTraceability traceability={traceability} />;
			},
		});
	}

	if (headersIncluded.includes('status')) {
		columns.push({
			label: t('status'),
			value: 'status',
			render: (status: TransactionStatus) => {
				return <ChipStatus status={status} />;
			},
		});
	}

	if (headersIncluded.includes('fromAccount')) {
		columns.push({
			label: t('fromAccount'),
			value: 'fromAccount',
			render: (fromAccount: Account) => {
				return <LinkAccount account={fromAccount} />;
			},
		});
	}

	if (headersIncluded.includes('toAccount')) {
		columns.push({
			label: t('toAccount'),
			value: 'toAccount',
			render: (toAccount: Account) => {
				return <LinkAccount account={toAccount} />;
			},
		});
	}

	if (headersIncluded.includes('amount')) {
		columns.push({
			label: t('amount'),
			value: 'amount',
			render: (amount: number, header: TableHeader, transaction: Transaction) => {
				const { displayAmount, displayConversions, displayCurrency } = getTransactionDisplayValue({
					currency,
					field: 'amount',
					loan,
					transaction,
				});

				return (
					<Amount
						conversionField="amount"
						conversions={hasConversions ? displayConversions : []}
						currency={displayCurrency}
						value={displayAmount}
					/>
				);
			},
		});
	}

	if (headersIncluded.includes('tax')) {
		columns.push({
			label: t('tax'),
			value: 'tax',
			render: (tax: number, header: TableHeader, transaction: Transaction) => {
				const { displayAmount, displayConversions, displayCurrency } = getTransactionDisplayValue({
					currency,
					field: 'tax',
					loan,
					transaction,
				});

				return (
					<Amount
						conversionField="tax"
						conversions={hasConversions ? displayConversions : []}
						currency={displayCurrency}
						value={displayAmount || 0}
					/>
				);
			},
		});
	}

	if (headersIncluded.includes('total')) {
		columns.push({
			label: t('total'),
			value: 'total',
			render: (total: number, header: TableHeader, transaction: Transaction) => {
				const { displayAmount, displayConversions, displayCurrency } = getTransactionDisplayValue({
					currency,
					field: 'total',
					loan,
					transaction,
				});

				return (
					<Amount
						conversionField="total"
						conversions={hasConversions ? displayConversions : []}
						currency={displayCurrency}
						value={displayAmount}
					/>
				);
			},
		});
	}

	if (headersIncluded.includes('reimbursed')) {
		columns.push({
			label: t('reimbursed'),
			value: 'hasReimbursement',
			render: (hasReimbursement: boolean, header: TableHeader, transaction: Transaction) => {
				const { displayAmount, displayConversions, displayCurrency } = getTransactionDisplayValue({
					currency,
					field: 'reimbursed',
					loan,
					transaction,
				});

				return (
					<Amount
						conversionField="amount"
						conversions={hasConversions ? displayConversions : []}
						currency={displayCurrency}
						TypographyProps={{
							color: displayAmount ? 'error.main' : undefined,
						}}
						value={displayAmount ? displayAmount * -1 : undefined}
					/>
				);
			},
		});
	}

	if (headersIncluded.includes('applied')) {
		columns.push({
			label: t('applied'),
			value: 'transactions',
			render: (transactions: Transaction[], header: TableHeader, transaction: Transaction) => {
				const { displayAmount, displayConversions, displayCurrency } = getTransactionDisplayValue({
					currency,
					field: 'applied',
					loan,
					transaction,
				});

				return (
					<Amount
						conversionField="amount"
						conversions={hasConversions ? displayConversions : []}
						currency={displayCurrency}
						TypographyProps={{
							color: displayAmount ? 'success.main' : undefined,
						}}
						value={displayAmount}
					/>
				);
			},
		});
	}

	if (headersIncluded.includes('updated')) {
		columns.push({
			label: t('updated'),
			value: 'modified',
			render: (modified: string, header: TableHeader, transaction: Transaction) => {
				return (
					<UserAvatarModified
						transaction={transaction}
						activityTypes={['TRANSACTION_UPDATED', 'TRANSACTION_CREATED']}
					/>
				);
			},
		});
	}

	return columns;
};
